import React, { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { Box, Chip, FormControl, Checkbox, Select, ListItemText, ListItemIcon, ListItem, List, Paper, MenuItem, InputLabel , TextField, Typography} from "@mui/material";
import { TranslationEnum, usePortalTranslation } from '../../../providers/TranslationProvider';

/**
 * It is a Generic Component that renders a searchbar and options
 * handleChange : It is called when any option from the list is selected
 * multiple : if you want the multiple option to be selected
 * handleSearch : When user want to perform some action when searchfield changes
 * showSelectedAsChip: if user want to show the selected options in chips
 * selectedOptions : options that are selected
 * countOfListItem: number of options to show ( Note: searching would be implement on all the options not on count of listItem)
 * @param param0 
 * @returns 
 */
const CustomSelectWithSearch = (props: {
    options: { value: string, label: string, disabled?: boolean}[],
    label: string,
    handleChange: (selectedItem: Array<any>) => any,
    selectedOptions: any[],
    handleSearch?: (e: any) => any,
    ListComponentProps?: object,
    loading?: boolean,
    showSelectedAsChip?: boolean
    multiple?: boolean,
    searchText:string,
    countOfListItem?:number | undefined,
    loadingIcon?:string| React.ReactNode,
    sort?:boolean,
    enableSelectAll?:boolean
    chipLimit?:number,
    showSearchBar?:boolean,
    showBorderInChip?:boolean,
    isDropdownView?:boolean;
    readOnly?: boolean;
}) => {
    const {
        options,
        label,
        handleChange,
        selectedOptions,
        handleSearch,
        ListComponentProps,
        loading,
        showSelectedAsChip,
        multiple,
        searchText,
        loadingIcon,
        countOfListItem,
        sort,
        enableSelectAll,
        chipLimit,
        showSearchBar = true,
        showBorderInChip = false,
        isDropdownView = false,
        readOnly = false
    } = props;
    const { translate } = usePortalTranslation();
    const CUR_COMPONENT_COMMON="commonPortal";
    const [filteredOptions,setFilteredOptions] = useState<Array<{label: string, value: string, disabled?: boolean}>>([]);
    const [isMultiple , setIsMultiple] = useState<boolean | undefined>(multiple);
   
    const isAllSelected = filteredOptions.length > 0 && selectedOptions.length >= filteredOptions.length;

    const scrollableDivRef = useRef();

    const handleToggle = (value: string, event?: React.MouseEvent) => {
      if (readOnly) return;

        if (event) {
            event.stopPropagation(); 
          }
        const option = options.find(opt => opt.value === value);
        if (option?.disabled) {
            return; 
        }

        if(enableSelectAll && value === "select-all"){
            let newSelection = selectedOptions.length >= filteredOptions.length ? []: filteredOptions.map((item)=>item.value);
            handleChange(newSelection);
            return;
        }


        const currentIndex = selectedOptions.indexOf(value);
        let newSelectedOptions = [];

        if (isMultiple) {
            newSelectedOptions = [...selectedOptions];
            if (currentIndex === -1) {
                newSelectedOptions.push(value);
            } else {
                newSelectedOptions.splice(currentIndex, 1);
            }
        } else {
            if (currentIndex === -1) {
                newSelectedOptions.push(value);
            } else {
                newSelectedOptions = [];
            }
        }

        handleChange(newSelectedOptions)
    };

    const handleSeachTextChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e || !e?.target) return;
        handleSearch && handleSearch(e);
    }

    useEffect(() => {
        setFilteredOptions(() => {
            const newOptions = options?.filter((option: { label: string, value: string }) =>
                option.value?.toLowerCase()?.includes(searchText?.toLowerCase()) || option.label?.toLowerCase()?.includes(searchText?.toLowerCase())
            ).slice(0, countOfListItem)
            return sort ? newOptions.sort((a, b) => a.label.localeCompare(b.label)) : newOptions;
        })
    }, [options, searchText]);

    useEffect(() => {
        setIsMultiple(multiple);
    }, [multiple])

    if (isDropdownView) {
        return (
          <div className="CustomSelectWithSearchWrapper">
            <FormControl
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: '32px',
                },
              }}
            >
              <InputLabel
                shrink={false}
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(0, 0, 0, 0.6)',
                  pointerEvents: 'none',
                  display: selectedOptions.length > 0 ? 'none' : 'block',
                }}
              >
                {label}
              </InputLabel>
              <Select
                multiple={multiple}
                value={multiple ? selectedOptions : selectedOptions[0] || ''}
                onChange={(e) => handleChange(multiple ? (e.target.value as string[]) : [e.target.value as string])}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: '28px' }}>
                    {(multiple ? (selected as string[]) : [selected as string]).map((value) => {
                      const option = options.find((opt) => opt.value === value);
                      return (
                        <Chip
                          key={value}
                          label={option?.label || value}
                          onDelete={(e) => {
                            e.stopPropagation();
                            handleToggle(value);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {enableSelectAll && (
                  <MenuItem
                    key="select-all"
                    value="select-all"
                    sx={{ background: isAllSelected ? '#e9f7fc' : 'inherit' }}
                    onClick={() => handleToggle('select-all')}
                  >
                    <Checkbox checked={isAllSelected} disabled={readOnly}/>
                    <ListItemText primary="Select All" />
                  </MenuItem>
                )}
                {filteredOptions.map((option) => {
                  const isSelected = selectedOptions.includes(option.value);
                  return (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled || readOnly}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {multiple && (
                        <Checkbox
                          checked={isSelected}
                          disabled={option.disabled || readOnly}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(option.value);
                          }}
                        />
                      )}
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
        );
      }

    return (
        <div className='CustomSelectWithSearchWrapper'>
            {showSearchBar ? 
            <TextField
                label={label}
                size='small'
                variant="outlined"
                placeholder='Search'
                fullWidth
                value={searchText}
                onChange={handleSeachTextChange}
            /> : <></> }
            {
                showSelectedAsChip && <div
                className='custom-select-with-search-selected-values-chips-wrapper'
                style={{
                  padding: selectedOptions.length > 0 ? '6px' : '0',
                  ...(showBorderInChip && selectedOptions.length > 0
                    ? { border: "1px solid #E5E5E5", borderRadius: "6px" }
                    : {}),
                }}
              >
                    <div className="custom-select-with-search-values-wrapper">
                        {
                            selectedOptions.length > 0 && (chipLimit ? selectedOptions.slice(0, chipLimit) : selectedOptions).map((op) => {
                                return (
                                    <Chip 
                                        key={op}
                                        label={op}
                                        onDelete={() => { handleToggle(op) }}
                                        disabled={readOnly}
                                    />
                                );
                            })
                        }
                    </div>
                </div>
            }
            <Paper {...ListComponentProps} id='custom-select-with-search-scrollable-div'>
                <List sx={{padding:0}}>
                    {
                        loading ?
                            <ListItem disabled>{loadingIcon ? loadingIcon : 'Loading...'}</ListItem>
                            :
                            filteredOptions.length === 0 ?
                                <ListItem disabled>
                                   {translate(TranslationEnum.common_portal,"No options")}
                                </ListItem>
                                : 
                                <>
                                    {
                                        enableSelectAll && <ListItem
                                            key={'select-all'}
                                            sx={{ background: isAllSelected ? '#e9f7fc' : 'inherit', cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.6 : 1 }}
                                            onClick={() => handleToggle('select-all')}
                                        >

                                            {isMultiple && <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={isAllSelected}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    disabled={readOnly}
                                                />
                                            </ListItemIcon>}
                                            <ListItemText primary={"Select All"} />
                                        </ListItem>
                                    }

{
        filteredOptions.map((option: { value: string, label: string, disabled?: boolean}) => {
            const selected = selectedOptions.indexOf(option.value) !== -1;
            
            return (
                <ListItem
                    key={option.value}
                    sx={{ 
                        background: selected ? '#e9f7fc' : 'inherit',
                        cursor: (option.disabled || readOnly) ? 'not-allowed' : 'pointer',
                        opacity: (option.disabled || readOnly) ? 0.6 : 1,
                    }}
                    onClick={() => !option.disabled && handleToggle(option.value)}
                >
                    {isMultiple && (
                        <ListItemIcon>
                            <Checkbox
                                edge="start"
                                checked={selected}
                                tabIndex={-1}
                                disableRipple
                                disabled={option.disabled || readOnly}
                            />
                        </ListItemIcon>
                    )}
                    <ListItemText 
                        primary={option.label} 
                        sx={{ color: (option.disabled || readOnly) ? '#aaa' : 'inherit' }}
                    />
                </ListItem>
            );
        })
    }
                                </>
                    }
                </List>
            </Paper>
        </div>
    );
};

export default memo(CustomSelectWithSearch);